import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'
import { EventType, KeyType } from 'constants/enums'
import { ToggleAriaExpanded } from 'utils/aria'
import { CSSUtils } from 'utils/css'
import { TabIndexUtils } from 'utils/tab-index'
import { DOMUtils } from 'utils/dom'
import { Nullable } from 'constants/types'
import { ComponentBase } from 'utils/abstract-classes/component-base'

enum BackdropStatus {
    OPEN,
    CLOSE
}

export class ModalComponent extends ComponentBase {
    public static activated: Nullable<ModalComponent>

    private readonly focusAbleElementsSelector =
        'a, button, input, select, textarea, details, iframe, audio, video, [tabindex="0"]'
    private readonly modalDialogPrefix = 'modalDialog'

    private id: string
    private lastYposition: number
    private backdropEnabled = false

    private modal: HTMLElement
    private lastFocusAbleElement: Nullable<HTMLElement>
    private modalTitle: Nullable<HTMLHeadingElement>
    private closeBtn: HTMLButtonElement

    /**
     * Get the last element which is focus-able
     * @return Nullable<HTMLElement>
     */
    private get _lastFocusAbleElement(): Nullable<HTMLElement> {
        const focusAbleElements = this.getElements(
            this.focusAbleElementsSelector,
            this.modal
        )

        if (focusAbleElements.length > 1) {
            return focusAbleElements.slice(-1)[0]
        }

        return undefined
    }

    /**
     * Initializes listening for actions
     * requesting modals to be opened
     */
    public constructor(
        private readonly modalControl?: HTMLElement,
        id?: string
    ) {
        super()

        this.id =
            id ||
            DOMUtils.getAttr(modalControl, ATTR_NAME.main.modal.modalControl)
        if (!this.id) {
            return
        }

        const modal = this.getElement(`#${this.modalDialogPrefix}${this.id}`)
        const closeBtn = this.getElement<HTMLButtonElement>(
            `.${CLASS_NAME.modal.closeBtn}`,
            modal
        )
        const title = this.getElement<HTMLHeadingElement>(
            `.${CLASS_NAME.modal.title}`,
            modal
        )

        if (!modal || !title || !closeBtn || !this.body) {
            return
        }

        this.modal = modal
        this.modalTitle = title
        this.closeBtn = closeBtn
        this.lastFocusAbleElement = this._lastFocusAbleElement

        this.initControlActions()
        this.initModalActions()
    }

    /**
     * Initializes modal control actions
     */
    private initControlActions(): void {
        if (!this.modalControl) {
            return
        }
        this.modalControl.addEventListener(EventType.CLICK, () => {
            this.openModal()
        })
        this.modalControl.addEventListener(
            EventType.KEYDOWN,
            (e: KeyboardEvent) => {
                if (e.key == KeyType.ENTER) {
                    e.preventDefault()
                    this.openModal()
                }
            }
        )
    }

    /**
     * Initializes modal actions
     */
    private initModalActions(): void {
        this.backdropEnabled = DOMUtils.hasAttr(
            this.modal,
            ATTR_NAME.main.modal.modalUseBackdrop
        )

        // * attach events to custom buttons, first, last focusable element, escape key
        // close button exit
        this.closeBtn.addEventListener(EventType.CLICK, () => this.closeModal())
        this.closeBtn.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleCloseBtnKeyboard(e)
        )

        const additionalCloseBtns = this.getElements(
            `[${ATTR_NAME.main.modal.modalClose}]`,
            this.modal
        )

        if (additionalCloseBtns.length) {
            additionalCloseBtns.forEach((closeAction) => {
                closeAction.addEventListener(EventType.CLICK, () =>
                    this.closeModal()
                )
                closeAction.addEventListener(
                    EventType.KEYDOWN,
                    (e: KeyboardEvent) => {
                        if (e.key == KeyType.ENTER) {
                            e.preventDefault()
                            this.closeModal()
                        }
                    }
                )
            })
        }

        // focus trap an open modal
        if (this.lastFocusAbleElement) {
            this.lastFocusAbleElement.addEventListener(
                EventType.KEYDOWN,
                (e: KeyboardEvent) => this.handleLastElTab(e)
            )
        }

        // escape key exit modal
        document.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) =>
            this.handleEscapeKey(e)
        )
    }

    /**
     * handle the DOM updates to make a modal open
     * classes for style and animation
     * tabindex for a11y
     * aria-expanded for a11y
     * move focus out of modal for a11y
     * close the backdrop if enabled
     */
    public closeModal(): void {
        CSSUtils.addClass(this.modal, CLASS_NAME.modal.closed)
        CSSUtils.removeClass(this.modal, CLASS_NAME.modal.open)
        TabIndexUtils.hideTabIndex(this.modal)
        CSSUtils.removeClass(this.html, CLASS_NAME.modal.activated)
        TabIndexUtils.removeTabIndex(this.body)
        this.toggleBackdrop(BackdropStatus.CLOSE)
        window.scrollTo(0, this.lastYposition)
        ToggleAriaExpanded(this.modalControl)
        if (this.modalControl) {
            this.modalControl.focus()
        }

        ModalComponent.activated = undefined
    }

    /**
     * handle the DOM updates to make a modal open
     * classes for style and animation
     * tabindex for a11y
     * aria-expanded for a11y
     * move focus to title for a11y
     * open the backdrop if enabled
     * if first time opening the modal save the control button to the component
     * @param {modalBtn} Element optional
     */
    public openModal(): void {
        // persist the user's scroll position when applying fixed screen
        this.lastYposition = window.pageYOffset
        CSSUtils.addClass(this.modal, CLASS_NAME.modal.open)
        CSSUtils.removeClass(this.modal, CLASS_NAME.modal.closed)
        TabIndexUtils.removeTabIndex(this.modal)
        CSSUtils.addClass(this.html, CLASS_NAME.modal.activated)
        TabIndexUtils.hideTabIndex(this.body)
        if (this.modalTitle) {
            this.modalTitle.focus()
        }
        this.toggleBackdrop(BackdropStatus.OPEN)
        ToggleAriaExpanded(this.modalControl)

        ModalComponent.activated = this
    }

    /**
     *  when hitting escape close the modal
     *  @param {KeyboardEvent} e
     */
    private handleEscapeKey(e: KeyboardEvent): void {
        if (ModalComponent.activated !== this) {
            return
        }

        switch (e.key) {
            case KeyType.ESC:
            case KeyType.ESC_IE:
                this.closeModal()
                break
        }
    }

    /**
     * Close button is the first focusable element and exit method
     * handle keyboard events
     * when tabbing reverse on the first element, return focus to last element
     *  @param {KeyboardEvent} e
     */
    private handleCloseBtnKeyboard(e: KeyboardEvent): void {
        if (ModalComponent.activated !== this) {
            return
        }

        if (e.key == KeyType.ENTER) {
            e.preventDefault()
            this.closeModal()
            return
        } else if (
            e.key === KeyType.TAB &&
            e.shiftKey &&
            this.lastFocusAbleElement
        ) {
            // with shift == reverse
            e.preventDefault()
            this.lastFocusAbleElement.focus()
            return
        }
    }

    /**
     * when tabbing forward on the last element, return focus to first element
     *  @param {KeyboardEvent} e
     */
    private handleLastElTab(e: KeyboardEvent): void {
        if (ModalComponent.activated !== this) {
            return
        }

        if (e.key === KeyType.TAB && !e.shiftKey && this.closeBtn) {
            e.preventDefault()
            this.closeBtn.focus()
        }
    }

    /**
     * Toggle hidden attribute on or off depending on status
     * @param {status} string enum open or close, optional
     */
    private toggleBackdrop(status: BackdropStatus): void {
        if (!this.backdropEnabled) {
            return
        }

        if (status == BackdropStatus.OPEN) {
            CSSUtils.addClass(this.html, CLASS_NAME.modal.backdrop)
        } else {
            CSSUtils.removeClass(this.html, CLASS_NAME.modal.backdrop)
        }
    }
}
